<template>
	<div ref="sharePopover" class="calendar-share">
		<button
			type="button"
			class="calendar-action calendar-share-btn"
			:class="{ 'calendar-share-btn--active': isShared }"
			:data-tooltip="isShared ? 'Shared' : 'Share Calendar'"
			:aria-label="isShared ? 'Shared' : 'Share Calendar'"
			data-position="bottom"
			@click.prevent="onToggleClick"
		>
			<font-awesome-icon
				fixed-width
				:icon="['fal', 'share-alt']"
				aria-hidden="true"
			/>
			<span v-if="isShared" class="calendar-share-btn-label">Shared</span>
		</button>

		<transition name="fade">
			<div
				v-if="showPopover"
				class="calendar-share-popover"
				role="dialog"
				aria-label="Share calendar"
				@click.stop
			>
				<div class="custom-checkbox calendar-share-popover-checkbox">
					<input
						id="calendar-share-toggle"
						v-model="isShared"
						type="checkbox"
						:disabled="isUpdating || !calendar"
						@change="onShareChange"
					/>
					<label for="calendar-share-toggle">
						<span class="checkmark">
							<CheckIcon />
						</span>
						<span class="calendar-share-popover-copy">
							<span class="calendar-share-popover-title">
								Share this calendar
							</span>
							<span class="calendar-share-popover-description">
								Generate a public link recipients can open to
								view calendars.
							</span>
						</span>
					</label>
				</div>

				<div v-if="isShared" class="calendar-share-popover-body">
					<div
						class="calendar-share-popover-divider"
						aria-hidden="true"
					></div>

					<p class="calendar-share-popover-url-label">PUBLIC URL</p>
					<input
						class="calendar-share-popover-url"
						type="text"
						readonly
						:value="publicUrl"
					/>
					<button
						type="button"
						class="calendar-share-copy-btn"
						:disabled="!publicUrl || isUpdating"
						@click.prevent="onCopyLink"
					>
						Copy Link
					</button>
					<p class="calendar-share-popover-footer">
						Same link for every calendar — recipients pick which to
						view.
					</p>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CheckIcon from '@/components/Icons/CheckIcon.vue'
import util from '@/util'

const ESCAPE_KEY_CODE = 27
const CALENDAR_SHARE_BASE_URL = (
	process.env.VUE_APP_CALENDAR_SHARE_URL || ''
).trim()

/**
 * Share calendar popover for the schedule module toolbar.
 */
export default {
	components: {
		CheckIcon,
	},

	computed: {
		publicUrl() {
			const shortId = this.partner?.partner_short_id

			if (!shortId || !CALENDAR_SHARE_BASE_URL) {
				return ''
			}

			return `${CALENDAR_SHARE_BASE_URL}/${shortId}/calendars`
		},

		...mapGetters('partners', {
			partner: 'active',
		}),
	},

	methods: {
		addEventListeners() {
			document.addEventListener('keydown', this.onEscapeKeyDown)
			document.addEventListener('click', this.onOutsideClick)
		},

		async onCopyLink() {
			const copied = await util.copyToClipboard(this.publicUrl)

			if (copied) {
				this.$alert.success('Link copied to clipboard.')
				return
			}

			this.$alert.error('Unable to copy link. Please try again.')
		},

		onEscapeKeyDown(event) {
			if (event.keyCode === ESCAPE_KEY_CODE) {
				this.closePopover()
			}
		},

		onOutsideClick(event) {
			if (
				this.showPopover &&
				this.$refs.sharePopover &&
				!this.$refs.sharePopover.contains(event.target)
			) {
				this.closePopover()
			}
		},

		async onShareChange() {
			if (!this.partner?.id || !this.calendar?.id || this.isUpdating) {
				return
			}

			const shareCalendars = this.isShared
			const previousValue = !shareCalendars

			this.isUpdating = true

			try {
				await this.setCalendarShare({
					id: this.partner.id,
					calendarId: this.calendar.id,
					public_sharing_enabled: shareCalendars,
				})

				this.isShared = shareCalendars

				this.$alert.success(
					shareCalendars
						? 'Calendar sharing enabled.'
						: 'Calendar sharing disabled.'
				)
			} catch (error) {
				this.isShared = previousValue
				this.$alert.response(error)
			} finally {
				this.isUpdating = false
			}
		},

		onToggleClick() {
			this.showPopover = !this.showPopover

			setTimeout(() => {
				if (this.showPopover) {
					this.addEventListeners()
					return
				}

				this.removeEventListeners()
			}, 0)
		},

		closePopover() {
			this.showPopover = false
			this.removeEventListeners()
		},

		removeEventListeners() {
			document.removeEventListener('keydown', this.onEscapeKeyDown)
			document.removeEventListener('click', this.onOutsideClick)
		},

		...mapActions('partners', {
			setCalendarShare: 'setCalendarShare',
		}),
	},

	name: 'ShareCalendarPopover',

	props: {
		/**
		 * The calendar being shared.
		 *
		 * @type {Object}
		 */
		calendar: {
			type: Object,
			default: null,
		},
	},

	watch: {
		calendar: {
			immediate: true,
			handler(calendar) {
				if (!calendar || this.isUpdating) {
					return
				}

				if (calendar?.public_sharing_enabled !== null) {
					this.isShared = Boolean(calendar.public_sharing_enabled)
				}
			},
		},
	},

	beforeDestroy() {
		this.removeEventListeners()
	},

	data() {
		return {
			isUpdating: false,
			showPopover: false,
			isShared: false,
		}
	},
}
</script>
